import { getMarket, legMultiplier, riskTier, selectionLabel, type Fixture } from '@h2h/shared';
import { ArrowRight, Lock, Plus } from '../../../ui/icons';
import { cn } from '../../../../lib/cn';
import { shortTeam } from '../../../../lib/teams';
import { hasLeg, type DraftCard } from '../../../../lib/picks';
import { legDetailText } from './helpers';
import { ScoreStepper } from './ScoreStepper';
import { LOSS_RED, TIER_COLOR, TIER_TEXT } from '../tokens';

const TIER_BG: Record<string, string> = {
  SAFE: 'rgba(17,179,107,0.07)',
  MEDIUM: 'rgba(227,168,43,0.10)',
  BOLD: 'rgba(245,96,15,0.08)'
};

function LegOptionButton({
  label,
  multiplier,
  selected,
  onClick
}: {
  label: string;
  multiplier?: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'tap flex min-h-[42px] flex-1 items-center justify-center rounded-[11px] border px-2 py-2 text-center transition-[transform,background-color,border-color,color,box-shadow] duration-150 ease-ios-out active:scale-[0.98]',
        selected ? 'border-night bg-night text-white shadow-[0_8px_18px_-12px_rgba(0,0,0,0.45)]' : 'border-black/10 bg-white text-ink2'
      )}
    >
      {multiplier != null ? (
        <span className="flex flex-col items-center justify-center gap-0.5 leading-none">
          <span className="text-[13px] font-extrabold">{label}</span>
          <span className={cn('num text-[10px] font-extrabold', selected ? 'text-white/65' : 'text-ink3')}>
            ×{multiplier.toFixed(1)}
          </span>
        </span>
      ) : (
        <span className="text-[13px] font-extrabold">{label}</span>
      )}
    </button>
  );
}

function RemoveBoostButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap mt-2 flex w-full items-center justify-center rounded-[10px] border border-black/[0.08] bg-white py-[9px] text-[11px] font-extrabold tracking-[-0.01em] text-ink2 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset] active:bg-black/[0.04]"
    >
      Remove boost
    </button>
  );
}

/** One boost row: tap to add/toggle, then pick its selection. */
export function LegRow({
  marketId,
  card,
  fixture,
  expanded,
  disabled,
  gated,
  onToggle,
  onSetLeg,
  onRemoveLeg,
  onOpenScorer,
  scorerName
}: {
  marketId: string;
  card: DraftCard;
  fixture: Fixture;
  expanded: boolean;
  disabled: boolean;
  gated?: boolean;
  onToggle: () => void;
  onSetLeg: (selection: string, label?: string) => void;
  onRemoveLeg: () => void;
  onOpenScorer: () => void;
  scorerName?: string;
}) {
  const m = getMarket(marketId);
  if (!m) return null;
  const on = hasLeg(card, marketId);
  const leg = card.legs.find((l) => l.marketId === marketId);
  const mult = legMultiplier(marketId, leg?.selection);
  const tier = riskTier(mult);
  const home = shortTeam(fixture.homeTeam);
  const away = shortTeam(fixture.awayTeam);
  const exH = leg && m.kind === 'exact' ? Number(leg.selection.split('-')[0] ?? 0) : 0;
  const exA = leg && m.kind === 'exact' ? Number(leg.selection.split('-')[1] ?? 0) : 0;
  const setExact = (h: number, a: number) => onSetLeg(`${h}-${a}`);
  const dimAdd = disabled && !on;

  const showPicker = (expanded || (on && m.kind === 'exact')) && m.kind !== 'player' && !gated;

  return (
    <div
      className={cn(
        'rounded-[15px] border px-3.5 transition-[background-color,border-color,box-shadow] duration-150 ease-ios-out',
        showPicker ? 'pb-2.5 pt-2' : 'py-[13px]',
        gated ? 'border-dashed border-black/12 bg-[#FAFAFB] opacity-60' : on ? 'border-[1.5px]' : 'border-black/[0.08] bg-card',
        dimAdd && !gated && 'opacity-45'
      )}
      style={on && !gated ? { borderColor: `${TIER_COLOR[tier]}80`, background: TIER_BG[tier] } : undefined}
    >
      <button
        type="button"
        disabled={(disabled && !on) || gated}
        onClick={() => (m.kind === 'player' && !gated ? onOpenScorer() : onToggle())}
        className={cn('tap flex w-full items-center gap-[11px] text-left active:scale-[0.99]', gated && 'cursor-not-allowed')}
      >
        <span className="h-[9px] w-[9px] shrink-0 rounded-[3px]" style={{ background: gated ? '#C4C4CC' : TIER_COLOR[tier] }} />
        <div className="min-w-0 flex-1">
          <div className={cn('text-[13.5px] font-extrabold tracking-snug', gated ? 'text-ink3' : 'text-ink')}>{m.label.replace('?', '')}</div>
          {gated ? (
            <div className="mt-px text-[11px] font-medium text-ink4">Not available · this league</div>
          ) : (
            <div className="mt-px flex items-center gap-1.5 text-[11px] font-medium">
              <span className="truncate text-ink2">{on && leg ? legDetailText(marketId, leg.selection, scorerName, home, away) : m.shortLabel}</span>
              <span className="shrink-0 font-bold" style={{ color: LOSS_RED }}>· miss ÷{mult.toFixed(1)}</span>
            </div>
          )}
        </div>
        <span
          className="num shrink-0 rounded-[8px] px-2 py-1 text-[12px] font-extrabold"
          style={{
            background: gated ? '#EFEFF3' : TIER_COLOR[tier],
            color: gated ? '#A8A8B0' : TIER_TEXT[tier]
          }}
        >
          ×{mult.toFixed(1)}
        </span>
        {gated ? (
          <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[8px] bg-fill">
            <Lock width={12} height={12} strokeWidth={2.2} className="text-ink3" />
          </span>
        ) : on ? (
          <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[8px]" style={{ background: TIER_COLOR[tier] }}>
            <ArrowRight width={13} height={13} strokeWidth={2.8} className="rotate-[-90deg] text-white" />
          </span>
        ) : (
          <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[8px] bg-fill">
            <Plus width={13} height={13} strokeWidth={2.8} className={dimAdd ? 'text-ink4' : 'text-ink2'} />
          </span>
        )}
      </button>

      {showPicker && (
        <div className="mt-2 border-t border-black/[0.07] pt-2">
          {m.kind === 'exact' ? (
            <div className="flex items-center justify-center gap-3">
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] font-extrabold tracking-[0.04em] text-ink2">{home}</span>
                <ScoreStepper value={exH} onChange={(v) => setExact(v, exA)} />
              </div>
              <span className="self-end pb-0.5 text-[18px] font-extrabold text-ink4">:</span>
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] font-extrabold tracking-[0.04em] text-ink2">{away}</span>
                <ScoreStepper value={exA} onChange={(v) => setExact(exH, v)} />
              </div>
            </div>
          ) : (
            <div className="flex gap-1.5">
              {(m.options ?? []).map((o) => (
                <LegOptionButton
                  key={o.value}
                  label={selectionLabel(marketId, o.value, { home, away })}
                  multiplier={o.multiplier}
                  selected={leg?.selection === o.value}
                  onClick={() => onSetLeg(o.value)}
                />
              ))}
            </div>
          )}
          {on && <RemoveBoostButton onClick={onRemoveLeg} />}
        </div>
      )}

      {on && m.kind === 'player' && !gated && <RemoveBoostButton onClick={onRemoveLeg} />}
    </div>
  );
}
